/**
 * The alert. A short neutral pop rather than anything musical: a trader hears it fifty times a day, so
 * it has to register without demanding attention or wearing thin.
 *
 * It is modelled on the system pop every Mac already makes — measured rather than guessed: around 670Hz,
 * a decay of about 25ms, and a handful of quieter, slightly higher repeats over the next 170ms, the way
 * a dropped ball bounces. Synthesised here rather than shipping Apple's file: nothing to load, nothing
 * to license, and the pitch and length stay adjustable.
 */
let context = null

/**
 * The switch lives here rather than at each call site. A poll callback outlives the render that made it
 * and can be holding a setting from ten minutes ago; asking the module at the moment of playing is the
 * only version that cannot be stale, and it is one place to look when the room is unexpectedly noisy.
 */
let enabled = false

export const setAlertsEnabled = (on) => {
  enabled = on !== false
}

export const alertsEnabled = () => enabled

const audioContext = () => {
  if (!context) {
    const Ctor = window.AudioContext || window.webkitAudioContext
    if (!Ctor) {
      return null
    }
    context = new Ctor()
  }
  return context
}

// when each bounce lands, how loud it is, and how much higher it sits than the one before
const BOUNCES = [
  { at: 0, gain: 1, pitch: 1 },
  { at: 0.062, gain: 0.62, pitch: 1.04 },
  { at: 0.094, gain: 0.34, pitch: 1.08 },
  { at: 0.132, gain: 0.15, pitch: 1.12 },
  { at: 0.168, gain: 0.06, pitch: 1.16 }
]

const BASE_HZ = 670
const DECAY = 0.024

const bounce = (audio, at, frequency, volume) => {
  const oscillator = audio.createOscillator()
  oscillator.type = 'sine'
  oscillator.frequency.setValueAtTime(frequency, at)
  // the tail sags a little, which is what stops it sounding like a beep from a machine
  oscillator.frequency.exponentialRampToValueAtTime(frequency * 0.82, at + DECAY)

  const envelope = audio.createGain()
  envelope.gain.setValueAtTime(0.0001, at)
  // 1.5ms in: fast enough to read as a click, slow enough not to spit
  envelope.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume), at + 0.0015)
  envelope.gain.exponentialRampToValueAtTime(0.0001, at + DECAY)

  oscillator.connect(envelope)
  envelope.connect(audio.destination)
  oscillator.start(at)
  oscillator.stop(at + DECAY + 0.01)
}

export const playAlert = (volume = 0.3) => {
  // no override, not even for the test buttons: a switch that something can ignore is not a switch
  if (!enabled) {
    return false
  }

  const audio = audioContext()
  if (!audio) {
    return false
  }

  // a tab that has never been clicked leaves the context suspended, and resume only takes from a gesture
  if (audio.state === 'suspended') {
    audio.resume().catch(() => {})
  }

  const start = audio.currentTime + 0.01
  BOUNCES.forEach(({ at, gain, pitch }) => {
    bounce(audio, start + at, BASE_HZ * pitch, volume * gain)
  })

  return true
}

/**
 * Browsers refuse to make noise until the page has been clicked once. Calling this from any early click
 * gets the context out of "suspended" so the first real alert is not the one that gets swallowed.
 */
export const unlockAudio = () => {
  const audio = audioContext()
  if (audio && audio.state === 'suspended') {
    audio.resume().catch(() => {})
  }
}

export const audioState = () => {
  const Ctor = window.AudioContext || window.webkitAudioContext
  if (!Ctor) {
    return 'unsupported'
  }
  return context ? context.state : 'not started'
}
